import { useRef, useState, useEffect } from 'react';

import { useGetServerTimeZoneId } from '@legacy/apis/indexAPI';
import { useGetTimeTracker } from '@legacy/apis/timeTrackerAPI/active';
import { GET_TIME_TRACKER_KEY } from '@legacy/apis/timeTrackerAPI/utils';
import { TimeTrackerPanel } from '@legacy/components/layout/Header/components/TimeTrackerButton/components/TimeTrackerPanel';
import { TimeTrackerTooltipButton } from '@legacy/components/layout/Header/components/TimeTrackerButton/components/TimeTrackerTooltipButton';
import legacyStyles from '@legacy/components/layout/Header/components/TimeTrackerButton/TimeTrackerButton.module.scss';
import {
  useKeyboardControl,
  useTimeCountDown,
} from '@legacy/components/layout/Header/components/TimeTrackerButton/timeTrackerHookHelper';
import { TimeSpan } from '@legacy/components/layout/Header/components/TimeTrackerButton/types';
import { useOnClickOutside } from '@legacy/hooks/useOnClickOutside';
import {
  Badge,
  Button,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import scssVariables from '@styles/variables.module.scss';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import styles from './LegacyTimeTrackerButton.module.scss';
import { Icon } from '@/components/ui/Icon';
import { areaIcons } from '@/constants/areaIcons';

export const TimeTrackerTooltipButtonForTest = TimeTrackerTooltipButton;

export const LegacyTimeTrackerButton = () => {
  const { t } = useTranslation('header');
  const TimeTrackerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const useMobileNavigation = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    data: timeTrackerData,
    time,
    details,
    stopPath,
    error: timeTrackerError,
    isLoading,
  } = useGetTimeTracker();
  const { serverTimeZoneId, isSuccess: isServerTimeZoneIdSuccess } =
    useGetServerTimeZoneId();
  const qc = useQueryClient();

  const [isActive, setIsActive] = useState(false);
  const [timeSpan, setTimeSpan] = useState<TimeSpan | undefined>();

  const handleChangeActive = () =>
    setIsActive((prev) => {
      qc.invalidateQueries({
        queryKey: [GET_TIME_TRACKER_KEY],
      });
      return !prev;
    });
  const getTabIndex = () => (isActive ? 0 : undefined);
  const handleClickOutside = () => setIsActive(false);

  useOnClickOutside(TimeTrackerRef, handleClickOutside);

  useKeyboardControl(isActive, setIsActive, TimeTrackerRef?.current);

  useEffect(() => {
    if (!timeSpan && time) setTimeSpan(time);
  }, [time, timeSpan]);

  useTimeCountDown(
    timeTrackerData,
    serverTimeZoneId,
    setTimeSpan,
    isServerTimeZoneIdSuccess,
  );

  useEffect(() => {
    const handleIframeEvent = (event: MessageEvent) => {
      if (event?.data?.eventId === 'timeTrackerUpdated')
        qc.invalidateQueries({
          queryKey: [GET_TIME_TRACKER_KEY],
        });
    };
    window.addEventListener('message', handleIframeEvent);
    return () => {
      window.removeEventListener('message', handleIframeEvent);
    };
  }, [qc]);

  if (isLoading || timeTrackerError) {
    return null;
  }

  return (
    <div
      className={cx(
        legacyStyles.Wrapper,
        styles.LegacyTimeTrackerWrapperNewScope,
      )}
      ref={TimeTrackerRef}
      data-automation-id="TimeTracker"
      tabIndex={getTabIndex()}
    >
      <Tooltip title={t('TimeTrackerOpenButton')} placement="bottom">
        <Button
          color="secondary"
          aria-controls="time-tracker"
          aria-expanded={isActive}
          aria-pressed={isActive}
          onClick={handleChangeActive}
          data-automation-id="TopBarTimeTrackerButton"
          className={cx({
            [styles.TimeTrackerButtonNotActive]:
              !time || (useMobileNavigation && time),
          })}
        >
          <Stack direction={'row'} gap={0.5} alignItems="center">
            <Badge
              color="primary"
              variant="dot"
              invisible={!time || !useMobileNavigation}
            >
              <Icon
                name={areaIcons.timeTracker}
                color={scssVariables.ColorBlack100}
              />
            </Badge>
            {!!time && !useMobileNavigation && (
              <Typography variant="label" color="textSecondary">
                {`${time.hour.toLocaleString('en-US', { minimumIntegerDigits: 2 })}:${time.min.toLocaleString('en-US', { minimumIntegerDigits: 2 })}`}
              </Typography>
            )}
          </Stack>
        </Button>
      </Tooltip>

      <TimeTrackerPanel
        isActive={isActive}
        handleChangeActive={handleChangeActive}
        timeTrackerData={details}
        stopPath={stopPath}
      />
    </div>
  );
};
